// ---------------------------------------------------------------------------
// パスワードの強さ（2026-09-22 射場さんの依頼）
//   - 画面の「強さのバー」と API の「簡単すぎるパスワードは受け付けない」が同じ物差しを使う（ここに1か所だけ）
//   - 物差しは Mac／iPhone／Windows／Android のパスワード生成に合わせる：
//       文字の種類（大文字・記号）を強制しない。長さと「意味のなさ」で決める（NIST SP 800-63B と同じ考え方）
//       OS が作る強いパスワード（Safari 20文字、Chrome 15文字、Windows 12〜16文字）は「とても強い」になる
//   - 弾くのは 8文字未満・よくあるパスワード（password1、12345678、qwerty…）・数字だけ・
//     同じ文字や abc・123・キーボードの並びばかり・自分のメールアドレスが入っている もの
//   - 辞書は小さく（よくある単語だけ）。ライブラリ（zxcvbn 等 800KB）は入れない
// 2026-10-14 射場さんの決定（#185、docs/74 §4）：**英字と数字を両方含む**を必須に（参加・会員登録・再設定・変更・設定のすべて）。
//   今あるパスワードはそのまま使える（ログインでは見ない）＝次に変えるときから。
//   画面は強さのバーをやめ、条件を ✓ の箇条書きで出す（passwordChecks。8文字以上（現在 n文字）／英字と数字を両方含む／推測されにくい）
// ---------------------------------------------------------------------------

export const PASSWORD_MSG = {
  // 文は短く（画面のバーの下に1行で収まる長さ。API の 400 でも同じ文）。2026-09-22 射場さん「要約、分かりやすく」
  tooShort: "8文字以上にしてください",
  tooLong: "200文字までです",
  /** 英字（A〜Z・a〜z）と数字（0〜9）の両方が要る（#185） */
  needsMix: "英字と数字を両方含めてください",
  common: "よくあるパスワードは使えません",
  repeat: "同じ文字の繰り返しは使えません",
  sequence: "abc・123 のような並びは使えません",
  containsEmail: "メールアドレスの一部は入れないでください",
  simple: "もう少し長くしてください",
  /** 欄の下の案内（空のとき）。#92（決め2）：事実の前半だけ。おすすめの後半は近くの「？」へ（tip） */
  guide: "8文字以上・英字と数字を両方含む",
  /** ✓ の箇条書き（#185）。length は「現在 n文字」を後ろに足す（#186：「いま」→「現在」） */
  checkLength: "8文字以上",
  checkMix: "英字と数字を両方含む",
  checkGuess: "推測されにくい",
  /** 「？」に入れるおすすめ（パスワードを決める画面の「？」） */
  tip: "スマホやパソコンが提案する強いパスワードがおすすめです。",
} as const;

/** 0〜4。2 から使える */
export type PasswordScore = 0 | 1 | 2 | 3 | 4;
export const STRENGTH_LABEL: Record<PasswordScore, string> = { 0: "弱すぎます", 1: "弱い", 2: "ふつう", 3: "強い", 4: "とても強い" };

export type PasswordStrength = {
  score: PasswordScore;
  label: string;
  /** 使えるか（8文字以上で score 2 以上） */
  ok: boolean;
  /** 使えないときの理由（画面と API が同じ文を出す）。使えるときは null */
  reason: string | null;
  /** 目安のビット数（推定） */
  bits: number;
};

// よくあるパスワード（漏えいしたパスワードの上位から。小文字で比べる。前後の数字・記号を外した形も比べる）
const COMMON = new Set([
  "123456", "1234567", "12345678", "123456789", "1234567890", "12345", "123123", "123321", "654321", "987654321",
  "111111", "1111111", "11111111", "000000", "00000000", "121212", "112233", "123qwe", "1q2w3e4r", "1qaz2wsx", "zaq12wsx", "1q2w3e",
  "password", "passwd", "passw0rd", "p@ssw0rd", "p@ssword", "pass", "password1", "password123", "pa55word",
  "qwerty", "qwerty123", "qwertyuiop", "asdf", "asdfgh", "asdfghjkl", "zxcvbn", "zxcvbnm", "qazwsx", "abc123", "abcd1234", "abcdefg", "abcdefgh",
  "iloveyou", "admin", "admin123", "administrator", "root", "login", "welcome", "welcome1", "letmein", "master", "secret", "hello", "hello123",
  "monkey", "dragon", "princess", "football", "baseball", "soccer", "sunshine", "shadow", "superman", "batman", "trustno1", "michael", "jennifer",
  "aaaaaaaa", "computer", "internet", "samsung", "google", "apple", "windows", "starwars", "pokemon", "naruto", "doraemon",
  "sakura", "nippon", "japan", "tokyo", "osaka", "yamada", "tanaka", "suzuki", "takahashi", "genbaryoku", "genba", "mitsumori",
]);

// パスワードの中によく入る単語（続く文字は「意味がある」ので数を割り引く）
const WORDS = [
  "password", "pass", "word", "admin", "login", "user", "root", "test", "guest", "welcome", "hello", "love", "secret", "master",
  "qwerty", "asdf", "zxcv", "dragon", "monkey", "shadow", "sunshine", "princess", "football", "baseball", "soccer",
  "japan", "tokyo", "osaka", "nippon", "sakura", "yamada", "tanaka", "suzuki", "sato", "genba", "genbaryoku", "mitsumori", "seikyu", "kaisha",
  "company", "office", "summer", "winter", "spring", "autumn", "january", "december", "birthday", "happy", "family", "money", "house",
];

// キーボードの並び（横1列・縦の並び）。前から後ろ、後ろから前のどちらも
const KEYBOARD = ["qwertyuiop", "asdfghjkl", "zxcvbnm", "1234567890", "qwertzuiop", "azertyuiop", "1qaz2wsx3edc", "!@#$%^&*()"];

const LEET: Record<string, string> = { "@": "a", "4": "a", "0": "o", "1": "i", "!": "i", "3": "e", "$": "s", "5": "s", "7": "t", "+": "t", "8": "b" };

function unleet(s: string): string {
  return s.replace(/[@4013!$57+8]/g, (c) => LEET[c] ?? c);
}

/** 前後の数字・記号を外す（password1 → password、!secret99 → secret） */
function core(lower: string): string {
  return lower.replace(/^[^a-z]+/, "").replace(/[^a-z]+$/, "");
}

function isCommon(lower: string): boolean {
  const c = core(lower);
  const u = unleet(lower);
  const uc = core(u);
  return COMMON.has(lower) || (c.length >= 3 && COMMON.has(c)) || COMMON.has(u) || (uc.length >= 3 && COMMON.has(uc));
}

/** 同じ塊の繰り返し（abcabcabc、1212）なら塊の長さ。違えば null */
function repeatedBlock(s: string): number | null {
  for (let n = 1; n <= Math.floor(s.length / 2); n++) {
    const block = s.slice(0, n);
    if (block.repeat(Math.ceil(s.length / n)).slice(0, s.length) === s) return n;
  }
  return null;
}

/**
 * 強さの見積もり。
 *   1文字ごとの「重み」：ふつう 1／直前と同じ 0.15／直前の隣の文字（abc・123・cba）0.35／キーボードの並びの続き 0.3／辞書の単語の続き 0.5
 *   重みの合計 × log2(文字の種類の数) を目安のビット数とし、28／36／56／75 で 0〜4 に分ける
 */
export function passwordStrength(pw: string, opts: { email?: string | null | undefined } = {}): PasswordStrength {
  const out = (score: PasswordScore, bits: number, reason: string | null): PasswordStrength => ({
    score,
    label: STRENGTH_LABEL[score],
    ok: reason === null && score >= 2,
    reason,
    bits: Math.round(bits),
  });
  if (pw.length === 0) return out(0, 0, PASSWORD_MSG.tooShort);
  if (pw.length < 8) return out(0, 0, PASSWORD_MSG.tooShort);
  if (pw.length > 200) return out(0, 0, PASSWORD_MSG.tooLong);

  // 英字と数字の両方（#185。数字だけ・英字だけはここで弾く）
  if (!hasLetterAndDigit(pw)) return out(0, 0, PASSWORD_MSG.needsMix);

  const lower = pw.toLowerCase();
  if (isCommon(lower)) return out(0, 0, PASSWORD_MSG.common);

  // 文字の種類
  let pool = 0;
  if (/[a-z]/.test(pw)) pool += 26;
  if (/[A-Z]/.test(pw)) pool += 26;
  if (/\d/.test(pw)) pool += 10;
  if (/[ -/:-@[-`{-~]/.test(pw)) pool += 33;
  if (/[^ -~]/.test(pw)) pool += 100;   // 日本語など
  const perChar = Math.log2(Math.max(pool, 10));

  // 位置ごとの重み
  const w = new Array<number>(pw.length).fill(1);
  const tag = new Array<"" | "repeat" | "seq">(pw.length).fill("");
  for (let i = 1; i < pw.length; i++) {
    const d = pw.charCodeAt(i) - pw.charCodeAt(i - 1);
    if (d === 0) { w[i] = 0.15; tag[i] = "repeat"; }
    else if (Math.abs(d) === 1) { w[i] = 0.35; tag[i] = "seq"; }
  }
  // キーボードの並び（4文字以上）
  for (const row of KEYBOARD) {
    const rows = [row, row.split("").reverse().join("")];
    for (const r of rows) {
      for (let i = 0; i + 4 <= lower.length; i++) {
        let len = 0;
        while (i + len < lower.length && len < r.length && r.includes(lower.slice(i, i + len + 1))) len++;
        if (len >= 4) {
          for (let k = i + 1; k < i + len; k++) { if (w[k]! > 0.3) { w[k] = 0.3; tag[k] = "seq"; } }
          i += len - 1;
        }
      }
    }
  }
  // 辞書の単語（unleet した形でも）
  const ul = unleet(lower);
  for (const word of WORDS) {
    for (const s of new Set([lower, ul])) {
      let from = 0;
      for (;;) {
        const at = s.indexOf(word, from);
        if (at < 0) break;
        for (let k = at + 1; k < at + word.length; k++) if (w[k]! > 0.5) w[k] = 0.5;
        from = at + word.length;
      }
    }
  }
  // 塊の繰り返し（abcabcabc）
  const block = repeatedBlock(lower);
  let eff = w.reduce((a, b) => a + b, 0);
  if (block !== null) eff = Math.min(eff, block + 1);

  const bits = eff * perChar;
  const repeatCount = tag.filter((t) => t === "repeat").length;
  const seqCount = tag.filter((t) => t === "seq").length;

  const score: PasswordScore = bits < 28 ? 0 : bits < 36 ? 1 : bits < 56 ? 2 : bits < 75 ? 3 : 4;

  // 自分のメールアドレスの一部（@ の前、4文字以上）が入っている
  const email = (opts.email ?? "").trim().toLowerCase();
  if (email) {
    const local = email.split("@")[0] ?? "";
    const parts = [email, local, ...local.split(/[._\-+]/)].filter((p) => p.length >= 4);
    if (parts.some((p) => lower.includes(p))) return out(Math.min(score, 1) as PasswordScore, bits, PASSWORD_MSG.containsEmail);
  }

  if (score >= 2) return out(score, bits, null);
  if (block !== null && block <= 3) return out(score, bits, PASSWORD_MSG.repeat);
  if (repeatCount >= pw.length / 2) return out(score, bits, PASSWORD_MSG.repeat);
  if (seqCount >= pw.length / 2) return out(score, bits, PASSWORD_MSG.sequence);
  return out(score, bits, PASSWORD_MSG.simple);
}

/** 使えないときの文。使えるなら null（API の 400、画面の送る前の確認に） */
export function passwordProblem(pw: string, email?: string | null): string | null {
  return passwordStrength(pw, { email }).reason;
}

/** 英字（半角の A〜Z・a〜z）と数字（半角の 0〜9）を両方含むか（#185） */
export function hasLetterAndDigit(pw: string): boolean {
  return /[A-Za-z]/.test(pw) && /[0-9]/.test(pw);
}

/**
 * ✓ の箇条書きの1行（#185、docs/74 §3 ②）。state：ok＝満たしている／todo＝まだ（打っている途中。誤りにしない）／ng＝この条件で使えない
 *   reason＝ng のときの文（推測されにくい の行だけ。「よくあるパスワードは使えません」など。API の 400 と同じ文）
 */
export type PasswordCheck = { key: "length" | "mix" | "guess"; state: "ok" | "todo" | "ng"; label: string; reason: string | null };

/**
 * 画面の ✓ の箇条書き（参加・会員登録・再設定・変更・設定で同じ。#185）。物差しは passwordStrength と同じ＝3つとも ok なら API も通す。
 *   ① 8文字以上（現在 n文字） ② 英字と数字を両方含む ③ 推測されにくい（①② を満たしてから判定。よくあるパスワード・並び・メールアドレスの一部）
 */
export function passwordChecks(pw: string, opts: { email?: string | null | undefined } = {}): PasswordCheck[] {
  const n = [...pw].length;
  const lengthOk = pw.length >= 8 && pw.length <= 200;
  const mixOk = hasLetterAndDigit(pw);
  const s = passwordStrength(pw, opts);
  const guess: PasswordCheck =
    !lengthOk || !mixOk
      ? { key: "guess", state: "todo", label: PASSWORD_MSG.checkGuess, reason: null }
      : s.ok
        ? { key: "guess", state: "ok", label: PASSWORD_MSG.checkGuess, reason: null }
        : { key: "guess", state: "ng", label: PASSWORD_MSG.checkGuess, reason: s.reason ?? PASSWORD_MSG.simple };
  return [
    {
      key: "length",
      state: lengthOk ? "ok" : pw.length > 200 ? "ng" : "todo",
      label: `${PASSWORD_MSG.checkLength}（現在 ${n}文字）`,
      reason: pw.length > 200 ? PASSWORD_MSG.tooLong : null,
    },
    { key: "mix", state: mixOk ? "ok" : "todo", label: PASSWORD_MSG.checkMix, reason: null },
    guess,
  ];
}
